import React, { useState } from 'react';
import { GripVertical, Trash2, ChevronDown } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TYPE_STYLES = {
  round:    { bg: '#F9FAFB', border: '#E5E7EB',  label: null },
  row:      { bg: '#F9FAFB', border: '#E5E7EB',  label: null },
  note:     { bg: '#EFF6FF', border: '#BFDBFE',  label: '📝 Note' },
  tip:      { bg: '#FFFBEB', border: '#FDE68A',  label: '💡 Tip' },
  warning:  { bg: '#FEF2F2', border: '#FECACA',  label: '⚠️ Warning' },
  assembly: { bg: '#F0FDF4', border: '#BBF7D0',  label: '🧩 Assembly' },
};

export default function RoundRow({ round, sectionId, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: round.id });
  const style = TYPE_STYLES[round.type] || TYPE_STYLES.round;

  const isSpecial = ['note', 'tip', 'warning', 'assembly'].includes(round.type);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        background: style.bg,
        border: `1px solid ${style.border}`,
      }}
      className="flex items-start gap-2 px-3 py-2 rounded-xl group"
    >
      {/* Drag handle */}
      <div {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 flex-shrink-0 mt-1.5">
        <GripVertical size={14} />
      </div>

      {/* Label / Type */}
      <div className="flex flex-col gap-0.5 flex-shrink-0" style={{ width: '90px' }}>
        {isSpecial ? (
          <span className="text-xs font-semibold" style={{ color: '#6B5744' }}>{style.label}</span>
        ) : (
          <input
            value={round.label}
            onChange={e => onUpdate(round.id, { label: e.target.value })}
            placeholder="Round 1"
            className="text-xs font-semibold text-gray-700 bg-transparent border-b border-transparent focus:border-stone-400 outline-none w-full"
          />
        )}
        {/* Type switcher */}
        <div className="relative">
          <select
            value={round.type}
            onChange={e => onUpdate(round.id, { type: e.target.value })}
            className="text-xs text-gray-400 bg-transparent outline-none cursor-pointer appearance-none pr-3 w-full"
          >
            <option value="round">Round</option>
            <option value="row">Row</option>
            <option value="note">Note</option>
            <option value="tip">Tip</option>
            <option value="warning">Warning</option>
            <option value="assembly">Assembly</option>
          </select>
          <ChevronDown size={9} className="absolute right-0 top-0.5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Instruction textarea */}
      <textarea
        value={round.instruction}
        onChange={e => onUpdate(round.id, { instruction: e.target.value })}
        placeholder={
          round.type === 'tip' ? 'Add a helpful tip for makers…' :
          round.type === 'warning' ? 'Add a warning…' :
          round.type === 'note' ? 'Add a note…' :
          round.type === 'assembly' ? 'Describe assembly step…' :
          'sc 6 in MR, [inc] ×6. (12)'
        }
        rows={expanded ? 4 : 1}
        onClick={() => setExpanded(true)}
        onBlur={() => !round.instruction && setExpanded(false)}
        className="flex-1 text-sm text-gray-800 placeholder-gray-400 bg-transparent outline-none resize-none min-w-0"
      />

      {/* Stitch count — only for round/row */}
      {!isSpecial && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-gray-400">(</span>
          <input
            type="text"
            value={round.stitch_count}
            onChange={e => onUpdate(round.id, { stitch_count: e.target.value })}
            placeholder="0"
            className="text-xs font-mono font-semibold text-gray-700 bg-transparent outline-none text-center"
            style={{ width: '32px' }}
          />
          <span className="text-xs text-gray-400">)</span>
        </div>
      )}

      {/* Delete */}
      <button
        onClick={() => onRemove(round.id)}
        className="p-1 text-gray-200 hover:text-red-400 transition-all flex-shrink-0 opacity-0 group-hover:opacity-100"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}
